import { Map } from 'immutable';
import noop from 'lodash/noop';
import { type RouterState } from 'react-router';
import { createSelector } from 'reselect';

import { type State } from 'src/types';

import * as Constants from '../../constants';
import { ApplicationStateKeys } from '../../constants/ApplicationStateKeys';
import { SocialActions } from '../../constants/Social';
import { DeepLinkContentTypes, PandoraTypes, SourceTypes } from '../../constants/SourceTypes';
import { deepLinkParams, Urls } from '../../constants/Urls';
import { getInDomain } from '../../domains/externalUtils';
import * as ClientInfoUtils from '../../lib/ClientInfoUtils';
import {
  getDeepLinkingUrl,
  getWebPandoraIdsWithDoubleSlashUri,
} from '../../lib/configuration/TunerVarUtils';
import * as EntityUtils from '../../lib/EntityUtils';
import * as ListenerUtils from '../../lib/ListenerUtils';
import * as ShareUtils from '../../lib/ShareUtils';
import { getStore } from '../../store/StoreAccessor';
import Logger from '../../tools/Logger';
import { isMobileDevice, isMobileWeb } from '../CapabilitiesUtils';

import * as BackstageRouteUtils from './BackstageRouteUtils';
import * as CollectionRouteUtils from './CollectionRouteUtils';
import * as DiscoveryRouteUtils from './DiscoveryRouteUtils';
import * as PaymentsRouteUtils from './PaymentsRouteUtils';
import * as SearchRouteUtils from './SearchRouteUtils';
import {
  createHelpUrl,
  getCurrentPathnameFromState,
  getMobileRegisterWelcomePath,
  getMobileSignInWelcomePath,
  getMyMusicAllFilterPath,
  getNowPlayingPath,
  getSubscriptionWelcomePath,
  isLoginPath,
  isPathInNowPlayingPath,
  isRegisterPath,
} from './shared';

const log = Logger.child({ component: 'lib/RouteUtils' });

/**
 *
 * @param {string} path
 * @param {boolean} openWindow - open path in a new window/tab
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function navigate(path: string, openWindow = false) {
  if (openWindow) {
    window.open(path);
  } else {
    location.href = path;
  }
}

/**
 * Get the current path from React Router location, normalized to be absolute since
 * router disregards basename.
 *
 * @param {object} props - component props
 * @returns {string} current path
 */
export function getCurrentPathname(props: RouterState): string | null {
  if (!(props.location && props.location.pathname)) return null;
  const path = props.location.pathname;
  if (path[0] === '/') {
    return path;
  }
  return `/${path}`;
}

/**
 * Return the concatenated protcol, hostname and port
 *
 * @returns {string} - the origin of the window uri
 */
export function getOrigin(): string {
  if (!window.location.origin) {
    return (
      window.location.protocol +
      '//' +
      window.location.hostname +
      (window.location.port ? ':' + window.location.port : '')
    );
  }
  return window.location.origin;
}

export function constructFullUrl(pathname: string): string {
  return getOrigin() + pathname;
}

/**
 * Convenience methods to get/construct paths
 * to known areas of the application
 */

export function getRootPath(): string {
  return '/' as const;
}

export function getLandingPath(): string {
  return '/' as const;
}

export function getServiceErrorPath(): string {
  return '/serviceError' as const;
}

export function getGiftUrl(): string {
  return `${Urls.GIFTS}` as const;
}

export function getPremiumUrl(): string {
  return `${Urls.PREMIUM_LINK}` as const;
}

export function getFeedPath(): string {
  return '/feed' as const;
}

export function getUnsupportedBrowserUrl(_state: State): string | undefined {
  if (window.location.hostname === 'www.pandora.com') {
    // production
    return 'https://www.pandora.com/unsupportedBrowser' as const;
  }
}

export const allowlistPandoraAdBlockerHelpUrl = createHelpUrl(
  '/s/article/Allowlist-Pandora-with-your-Ad-Blocker',
);

export function getAutoplayBlockHelperUrl(): string {
  const browser = ClientInfoUtils.getBrowserInfo().name;
  switch (browser) {
    case 'Chrome':
      return createHelpUrl(
        '/s/article/Chrome-Troubleshooting-1519949296244?language=en_US#autoplay',
      );
    case 'Firefox':
      return createHelpUrl(
        '/s/article/Firefox-Troubleshooting-1519949297419?language=en_US#autoplay',
      );
    case 'Safari':
      return createHelpUrl(
        '/s/article/Safari-Troubleshooting-1519949305254?language=en_US#autoplay',
      );
    case 'Edge':
      return createHelpUrl('/s/article/Edge-Troubleshooting?language=en_US#autoplay');
    default:
      log.warn(
        `Couldn't find help page to educate user when browser blocked autoplay for browser: ${browser}`,
      );
  }
  return '';
}

export function getEverywhereBusinessUrl(): string {
  return `${Urls.BUSINESS}` as const;
}

/* Route Mutation Utilities - End */

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getRouterCurrentLocation(state: State) {
  return state.routing.locationBeforeTransitions;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getQueries(state: State) {
  return state.application.get(ApplicationStateKeys.STARTUP_QUERY_PARAMETERS);
}

export function isMobileRegisterWelcomePath(pathname: string): boolean {
  return /^\/account\/register\/welcome\/?$/.test(pathname);
}

export function isRootPath(path: string): boolean {
  return /^\/?$/g.test(path);
}

export function getNextMobilePath(currentPath: string): string {
  if (isLoginPath(currentPath)) {
    return getMobileSignInWelcomePath();
  } else if (isRegisterPath(currentPath)) {
    return getMobileRegisterWelcomePath();
  } else if (isRootPath(currentPath)) {
    return getRootPath();
  }
  return getSubscriptionWelcomePath();
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getNextDefaultPath(state: State, isAnon?: boolean) {
  if (isAnon) {
    return getLandingPath();
  }

  if (isMobileDevice(state)) {
    const currentPath = getCurrentPathnameFromState(state);
    return getNextMobilePath(currentPath);
  }

  let nextDefaultPath;

  if (ListenerUtils.hasPremiumFeatures(state)) {
    nextDefaultPath = getMyMusicAllFilterPath();
  } else {
    nextDefaultPath = getNowPlayingPath(state);
  }

  return nextDefaultPath;
}

/**
 * Get the next path to redirect to after Authentication
 *
 * @param {object} state - store state
 * @param {boolean} isAnon - is anonymous login
 * @returns {string} path to redirect to
 */
export function getNextPath(state: State, isAnon?: boolean): string {
  const redirect = ListenerUtils.getLoginRedirectOnSuccess(state);

  if (!redirect || redirect === getRootPath()) {
    return getNextDefaultPath(state, isAnon);
  }

  return redirect;
}

export function getColonSlash(sourceId: string): string {
  const store = getStore();
  if (store) {
    const state = store.getState();
    const allowlist = getWebPandoraIdsWithDoubleSlashUri(state).split(',');
    if (allowlist[0] === SocialActions.ALLOWLIST_ALL || allowlist.includes(sourceId)) {
      return '://' as const;
    }
  }
  return ':/' as const;
}

export function getDesktopLandingPath(): string {
  return '/open' as const;
}

interface IDeepLinkParams {
  sourceType: string;
  sourceId: string;
  listenerId: string;
  allParams: string;
  pageUrl: string;
}

/**
 * Get source deep link
 *
 * @param {string} sourceType
 * @param {string} sourceId/pandoraId
 * @param {string} listenerId
 * @param {string} allParams
 * @param {string} pageUrl
 * @returns {string} deep link to source backstage/station page
 */

export function getDeepLinkPath({
  sourceType = '',
  sourceId,
  listenerId,
  allParams,
  pageUrl = '',
}: IDeepLinkParams): string {
  // $TSFixMe define more specific string return
  const anonWebLID = listenerId ? `anonWebLID=${listenerId}` : '';

  if (sourceId) {
    switch (sourceType) {
      case SourceTypes.STATION:
      case PandoraTypes.STATION:
        return `pandorav2${getColonSlash(
          sourceId,
        )}createStation?stationId=${sourceId}&${anonWebLID}${allParams}`;
      case SourceTypes.SONG:
      case PandoraTypes.SONG:
        return `pandorav4${getColonSlash(
          sourceId,
        )}backstage/track?token=${sourceId}&${anonWebLID}${allParams}`;
      case SourceTypes.ALBUM:
      case PandoraTypes.ALBUM:
        return `pandorav4${getColonSlash(
          sourceId,
        )}backstage/album?token=${sourceId}&${anonWebLID}${allParams}`;
      case SourceTypes.PLAYLIST:
      case PandoraTypes.PLAYLIST:
        return `pandorav5${getColonSlash(sourceId)}playlist/${sourceId}?${anonWebLID}${allParams}`;
      case SourceTypes.GENRE:
      case PandoraTypes.GENRE:
        return `pandorav2${getColonSlash(
          sourceId,
        )}openGenrePicker?pandoraId=${sourceId}&${anonWebLID}${allParams}`;
      case SourceTypes.ARTIST:
      case PandoraTypes.ARTIST:
        return `pandorav4${getColonSlash(
          sourceId,
        )}backstage/artist?token=${sourceId}&${anonWebLID}${allParams}`;
      case SourceTypes.CURATOR:
      case PandoraTypes.CURATOR:
        return `pandorav10${getColonSlash(
          sourceId,
        )}backstage/curator?token=${sourceId}?${anonWebLID}${allParams}`;
      case SourceTypes.PODCAST:
      case PandoraTypes.PODCAST:
      case SourceTypes.EPISODE:
      case PandoraTypes.EPISODE:
        return `pandorav9${getColonSlash(sourceId)}podcast/${sourceId}?${anonWebLID}${allParams}`;
      default:
        return 'N/A';
    }
  }

  if (DiscoveryRouteUtils.isBrowsePath(pageUrl)) {
    return `pandorav6://browse?${anonWebLID}${allParams}`;
  }

  if (pageUrl === PaymentsRouteUtils.getAccountBillingUniversalLinkPathname()) {
    return PaymentsRouteUtils.getDeepLinkAccountBilling();
  }

  return 'pandorav2://';
}

export function getAllParams(state: State, isFirstParam = false): string {
  let params: $TSFixMe = getQueries(state);
  let allParams = '';
  const firstChar = isFirstParam ? '?' : '&';
  const isMobileWebClient = isMobileWeb(state);

  if (isMobileWebClient && params.get('part') && params.get('corr')) {
    params = Map({ part: params.get('part'), corr: params.get('corr') });
  }
  params.forEach((value: $TSFixMe, key: $TSFixMe) => {
    allParams += `${firstChar}${key}=${value}`;
  });

  return allParams;
}

export function getMobileLongLink(
  state: State,
  { pageUrl = '', feature = '', type = '', id = '' } = {},
): string {
  const isAnon = ListenerUtils.isAnonymousListener(state);
  const listenerId = ListenerUtils.getCurrentListenerIdFromState(state);
  // Use proper url param syntax, depending on anonWebLID availability
  const firstParam = isAnon && listenerId ? `anonWebLID=${listenerId}&` : '';

  const deepLinkingUrl = getDeepLinkingUrl(state);
  const allParams = getAllParams(state, false);
  const channelParam = encodeURIComponent(deepLinkParams.MOBILE_WEB);
  const featureParam = encodeURIComponent(feature);
  const { pandoraId, sourceType } = BackstageRouteUtils.getPandoraIdAndSourceTypeFromBackstagePath(
    state,
    pageUrl,
  );
  const sourceId = (id || pandoraId) ?? '';
  const deeplinkType = type || (sourceType as string); // $TSFixMe
  const deepLinkPath = getDeepLinkPath({
    allParams,
    listenerId,
    pageUrl,
    sourceId,
    sourceType: deeplinkType,
  });
  const encodedDeepLinkPath = encodeURIComponent(deepLinkPath);
  const desktopUrl = `https://www.pandora.com${pageUrl}${allParams}`;
  const encodedDesktopUrl = encodeURIComponent(desktopUrl);

  return `${deepLinkingUrl}?${firstParam}~channel=${channelParam}&~feature=${featureParam}&$deeplink_path=${encodedDeepLinkPath}${allParams}&$desktop_url=${encodedDesktopUrl}`;
}

interface IShareDeepLinkDataParams {
  shareURL: string;
  sourcePath: string;
  sourceType: keyof typeof DeepLinkContentTypes;
  imgUrl: string;
  genreStationName: string;
  channelType?: string;
  sourceLocation?: string;
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getShareDeepLinkData(
  state: State,
  {
    shareURL,
    sourcePath,
    sourceType,
    imgUrl,
    genreStationName,
    channelType,
    sourceLocation,
  }: IShareDeepLinkDataParams,
) {
  const currentPathname = getCurrentPathnameFromState(state);
  let feature = null;
  if (BackstageRouteUtils.isBackstagePath(currentPathname)) {
    feature = deepLinkParams.BACKSTAGE_PAGE;
  } else if (isPathInNowPlayingPath(currentPathname)) {
    feature = deepLinkParams.NOW_PLAYING;
  } else if (
    CollectionRouteUtils.isCollectionPath(currentPathname) ||
    CollectionRouteUtils.isMyMusicPath(currentPathname)
  ) {
    feature = deepLinkParams.COLLECTION;
  } else if (DiscoveryRouteUtils.isBrowsePath(currentPathname)) {
    feature = deepLinkParams.BROWSE;
  } else if (SearchRouteUtils.isInSearchPath(currentPathname)) {
    feature = deepLinkParams.SEARCH;
  }

  const contentType = DeepLinkContentTypes[sourceType];
  const listenerId = ListenerUtils.getCurrentListenerIdFromState(state);
  const activeTier = getInDomain('payments.selectors.getActiveProductGroupTier')(state);
  const listenerTier = deepLinkParams[activeTier as keyof typeof deepLinkParams] || null;
  const clientVersion = ClientInfoUtils.getCurrentClientVersion();

  // Branch currently scrapes incorrect meta tags so we have to set them manually
  const metaDetails = ShareUtils.getMetaTagDetailsForURL(state, sourcePath, genreStationName);

  return {
    $desktop_url: shareURL,
    $og_description: metaDetails.description,
    $og_image_url: imgUrl,
    $og_title: metaDetails.title,
    '~channel':
      channelType === deepLinkParams.DESKTOP_APP
        ? deepLinkParams.DESKTOP_APP
        : deepLinkParams.USER_SHARED,
    '~feature': sourceLocation ?? feature,
    '~tags': [
      `Content Type - ${contentType}`,
      `Shared From LID - ${listenerId}`,
      `Shared From Tier - ${listenerTier}`,
      `Shared From Platform - ${deepLinkParams.DESKTOP}`,
      `Shared From Webclient Version - ${clientVersion}`,
    ],
  };
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function copyUrlToClipboard(url: string) {
  const urlTextArea = document.createElement('textArea') as HTMLInputElement;
  urlTextArea.style.fontSize = '16px'; // prevents zoom on iOS
  urlTextArea.style.opacity = '0'; // prevents scroll for some reason
  urlTextArea.value = url;
  document.body.appendChild(urlTextArea);

  const range = document.createRange();
  range.selectNodeContents(urlTextArea);

  const selection = window.getSelection();
  selection?.removeAllRanges();
  selection?.addRange(range);
  urlTextArea.setSelectionRange(0, 999999);

  document.execCommand('copy');
  document.body.removeChild(urlTextArea);
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getAndroidDeepLinkOnClick(mobileDeepLink: string) {
  const deferredDeepLink = encodeURIComponent(`utm_source=pandora&utm_content=${mobileDeepLink}`);
  const appStoreUrl = Constants.Onboarding.PurchaseLinks.GOOGLE_DEEPLINK + deferredDeepLink;
  const mobileDeepLinkRegexCaptureGroups = mobileDeepLink.match(/pandorav(\d):\/*(\/.*)/);
  const version = mobileDeepLinkRegexCaptureGroups?.[1];
  const command = mobileDeepLinkRegexCaptureGroups?.[2];
  const androidChromeIntent = `intent:${command}#Intent;scheme=pandorav${version};package=com.pandora.android;S.market_referrer=${deferredDeepLink};end`;

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  return () => {
    const userAgent = navigator && navigator.userAgent;
    const chrome = userAgent.match(/Chrome\/(\d+)\./);

    // For Chrome Android Intents. Android Intents
    // open the native app if installed. Otherwise, opens the app store.
    // https://developer.chrome.com/multidevice/android/intents
    if (chrome) {
      window.location.href = androidChromeIntent;
      return;
    }

    // For all other Android browsers and versions, fallback to this hack: Attempt
    // deep link. If after 1 second timeout the client is still in the foreground,
    // link to app store.
    if (appStoreUrl) {
      setTimeout(function () {
        // check whether the window has been backgrounded,
        // meaning that the mobile app was successfully opened
        // so we dont need to open the app store
        if (!document.webkitHidden) {
          window.location.href = appStoreUrl;
        }
      }, 1000);
    }

    if (mobileDeepLink) {
      window.location.href = mobileDeepLink;
    }
  };
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getIosDeepLinkOnClick(
  iOSCustomDeferredDeepLinkPathname: string | undefined,
  listenerIdFromState: string | undefined,
  allParams: string,
) {
  const appStoreUrl = Constants.Onboarding.PurchaseLinks.ITUNES;
  const anonWebLID = listenerIdFromState ? `?anonWebLID=${listenerIdFromState}` : '';

  const iosPathname = iOSCustomDeferredDeepLinkPathname || window.location.pathname;
  const iOSDeferredDeepLink = `${window.location.origin}${iosPathname}${anonWebLID}${allParams}`;

  // For iOS, always link to app store with deferred deep link to avoid error message
  // when native app is not installed.
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  return () => {
    copyUrlToClipboard(iOSDeferredDeepLink);
    window.location.href = appStoreUrl;
  };
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getDeepLinkOnClick(
  state: State,
  mobileDeepLink = 'pandorav2://', // $TSFixMe
  iOSCustomDeferredDeepLinkPathname?: string,
) {
  const isAndroidClient = ClientInfoUtils.isAndroidClient();
  const isIOSClient = ClientInfoUtils.isIOSClient();

  if (isAndroidClient) {
    return getAndroidDeepLinkOnClick(mobileDeepLink);
  } else if (isIOSClient) {
    const listenerIdFromState = ListenerUtils.getCurrentListenerIdFromState(state);
    const allParams = getAllParams(state);
    return getIosDeepLinkOnClick(iOSCustomDeferredDeepLinkPathname, listenerIdFromState, allParams);
  }
  return noop;
}

export const shouldShowLanding = createSelector(
  (state: State) => {
    return getCurrentPathnameFromState(state);
  },
  (pathname: string) => {
    return getLandingPath().indexOf(pathname) !== -1;
  },
);

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function getPandoraIdFromSourceURL(state: State, urlPath?: string) {
  if (urlPath) {
    const searchedPath = (urlPath || '').toLowerCase();
    const annotations = EntityUtils.getAllAnnotations(state);
    const annotation =
      annotations &&
      annotations.find((candidate: $TSFixMe) => {
        const candidatePath = (candidate.get('shareableUrlPath') || '').toLowerCase();
        return candidatePath === searchedPath;
      });
    return annotation && annotation.get('pandoraId');
  }
  return '';
}

export function getPandoraIdFromNowPlayingSourceURL(urlPath: string): string | undefined {
  if (urlPath) {
    const result = /[^/]*$/.exec(urlPath)?.[0];
    return result;
  }
  return '';
}

export function getPandoraIdFromGenreURL(
  state: State,
  trackId: string,
  getStationId: boolean,
): string {
  if (trackId) {
    const seed = getInDomain('playback.utils.getStationByStationId')(state, trackId);
    const initialSeed = seed && seed.get('initialSeed');
    const stationId = seed && seed.get('stationId');
    if (getStationId) {
      return stationId;
    }
    return initialSeed && initialSeed.replace(/^G/, 'GE:');
  }
  return '';
}
